import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useBranding } from "../lib/branding";
import { useAuth } from "../lib/auth";
import { useApi } from "../lib/useApi";
import { getKb } from "../lib/api";
import { matchArticles } from "../lib/kbSearch";
import { Markdown } from "../lib/markdown";
import { Loading } from "../components/States";

/** Help-center landing page: tenant welcome copy + a big search box that filters
 *  the published KB client-side (fetched once), category chips, and a grid of
 *  recent articles. Deflection starts here — search before you submit. */
export function HomePage() {
  const { settings, loading: brandingLoading } = useBranding();
  const { status } = useAuth();
  const welcome = settings.portal.welcome;

  const [query, setQuery] = useState("");
  const kb = useApi((signal) => getKb(undefined, signal), []);
  const articles = kb.data?.articles ?? [];
  const categories = kb.data?.categories ?? [];

  const results = useMemo(() => matchArticles(articles, query, 8), [articles, query]);
  const searching = query.trim().length >= 2;
  const topArticles = articles.slice(0, 6);

  return (
    <div className="home">
      <section className="hero">
        <h1>How can we help?</h1>
        {brandingLoading ? null : welcome ? (
          <div className="hero-welcome">
            <Markdown source={welcome} />
          </div>
        ) : (
          <p className="hero-sub">
            Search our knowledge base or submit a request and we’ll get back to you.
          </p>
        )}

        {/* Big search: filters the already-fetched KB list by title/body. */}
        <form
          className="hero-search"
          role="search"
          onSubmit={(e) => e.preventDefault()}
        >
          <span className="hero-search-icon" aria-hidden="true">⌕</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles — e.g. “reset my password”"
            aria-label="Search the knowledge base"
            disabled={kb.loading && !kb.data}
          />
        </form>

        {searching ? (
          <div className="hero-results" role="region" aria-live="polite" aria-label="Search results">
            {kb.loading ? (
              <Loading label="Searching…" />
            ) : results.length > 0 ? (
              <ul className="kb-article-list">
                {results.map((a) => (
                  <li key={a.id}>
                    <Link to={`/kb/${encodeURIComponent(a.slug)}`} className="kb-article-row">
                      <span className="kb-article-name">{a.title}</span>
                      <span className="kb-article-arrow" aria-hidden="true">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="hero-no-results">
                <p className="muted">
                  Nothing matched “{query.trim()}”.{" "}
                  {status === "authenticated" ? (
                    <Link to="/tickets/new">Submit a request</Link>
                  ) : (
                    <Link to="/signin">Sign in to submit a request</Link>
                  )}{" "}
                  and we’ll help directly.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="hero-actions">
            <Link to="/kb" className="btn primary">
              Browse the knowledge base
            </Link>
            {status === "authenticated" ? (
              <Link to="/tickets/new" className="btn">
                Submit a request
              </Link>
            ) : (
              <Link to="/signin" className="btn">
                Sign in to submit a request
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Category chips — quick jumps into the filtered KB index. */}
      {categories.length > 0 ? (
        <nav className="kb-cats home-cats" aria-label="Browse by category">
          {categories.map((c) => (
            <Link key={c.id} to={`/kb?category=${encodeURIComponent(c.slug)}`} className="chip">
              {c.name}
            </Link>
          ))}
        </nav>
      ) : null}

      <section className="home-section" aria-labelledby="popular-heading">
        <div className="section-head">
          <h2 id="popular-heading">Popular articles</h2>
          <Link to="/kb" className="see-all">
            See all
          </Link>
        </div>

        {kb.loading ? (
          <Loading label="Loading articles…" />
        ) : kb.error ? (
          <p className="muted">Articles are unavailable right now.</p>
        ) : topArticles.length === 0 ? (
          <p className="muted">No published articles yet.</p>
        ) : (
          <ul className="article-grid">
            {topArticles.map((a) => (
              <li key={a.id}>
                <Link to={`/kb/${encodeURIComponent(a.slug)}`} className="article-card">
                  <span className="article-card-title">{a.title}</span>
                  <span className="article-card-arrow" aria-hidden="true">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
